import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mail, Clock, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export const SupportModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/support", form);
      setDone(true);
      toast.success("Support request sent. We'll reply within hours.");
    } catch {
      toast.error("Could not send request. Email us directly.");
    } finally {
      setSending(false);
    }
  };

  const close = () => { setDone(false); setForm({ name: "", email: "", subject: "", message: "" }); onClose(); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg" data-testid="support-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-xl">
            <Mail className="w-5 h-5 text-emerald-400" /> Technical Support
          </DialogTitle>
          <DialogDescription className="text-slate-400 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-emerald-400" /> Available 24/24 · kaadeconstruction@gmail.com
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-10 text-center space-y-3" data-testid="support-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <p className="text-slate-200 font-medium">Request received</p>
            <p className="text-sm text-slate-400">Our engineers will reach out to {form.email || "you"} shortly.</p>
            <Button onClick={close} className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-950 border-slate-800" data-testid="support-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-slate-950 border-slate-800" data-testid="support-email" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Subject</Label>
              <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="bg-slate-950 border-slate-800" data-testid="support-subject" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Message</Label>
              <Textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-slate-950 border-slate-800" data-testid="support-message" />
            </div>
            <Button type="submit" disabled={sending} data-testid="support-submit"
              className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
              {sending ? "Sending…" : "Send request"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
